'use client'

import { useEffect, useState } from 'react'

import { useRouter } from 'next/navigation'

import Typography from '@mui/material/Typography'

import classnames from 'classnames'

import { signIn, useSession } from 'next-auth/react'

import {
  Autocomplete,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  TextField
} from '@mui/material'

import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'

import { DemoContainer, DemoItem } from '@mui/x-date-pickers/internals/demo'

import dayjs from 'dayjs'

import axios from 'axios'

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import themeConfig from '@configs/themeConfig'

import CustomButton from '@/components/layout/shared/CustomButton'
import LogoLogin from '@/components/layout/shared/LogoLogin'
import NumericFormatCustom from '@/components/NumericFormatCustom'
import getUrlApiLogin from '@/utils/getUrlApiLogin'
import { useImageVariant } from '@core/hooks/useImageVariant'
import { useSettings } from '@core/hooks/useSettings'

import useEncryptAES from '@/hooks/useEncryptAES'
import showToastAlert from '@components/Toast'

const dataShift = [
  { value: 0, label: 'Pagi' },
  { value: 1, label: 'Siang' },
  { value: 2, label: 'Malam' }
]

const Login = () => {
  const darkImg = '/images/pages/auth-v2-mask-1-dark.png'
  const lightImg = '/images/pages/auth-v2-mask-1-light.png'

  const { settings } = useSettings()

  const authBackground = useImageVariant('light', lightImg, darkImg)

  const router = useRouter()
  const { data: session, status } = useSession()

  const [isPasswordShown, setIsPasswordShown] = useState(false)
  const [loading, setLoading] = useState(false)

  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [open, setOpen] = useState(false)
  const [tglTransOrder, setTglTransOrder] = useState(dayjs().toString())
  const [selectedShift, setselectedShift] = useState('')
  const [saldo, setSaldo] = useState('')
  const [pegawai, setPegawai] = useState('')
  const [idUnit, setIdUnit] = useState('')
  const [idPegawai, setIdPegawai] = useState('')
  const [loadingSimpan, setLoadingSimpan] = useState<boolean>(false)
  const { encryptAES } = useEncryptAES()

  const handleClickShowPassword = () => setIsPasswordShown(show => !show)

  useEffect(() => {
    if (status === 'authenticated') {
      const redirectTo = '/kasir-home'

      router.push(redirectTo)
    }
  }, [status, session, router])

  const doLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!username) {
      showToastAlert('info', 'Username tidak boleh kosong')

      return
    }

    if (!password) {
      showToastAlert('info', 'Password tidak boleh kosong')

      return
    }

    setLoading(true)
    await clearAllCookies()

    const url = await getUrlApiLogin()

    const formData = {
      user: username,
      pass: password
    }

    const encryptedData = await encryptAES(JSON.stringify(formData))

    try {
      const response = await axios.post(url + 'login/loginadmin', {
        data: encryptedData
      })
      if (response.data.metadata.code === 200) {
        const session = response.data.response.list[0]

        if (session) {
          const idJabatan = session?.ID_JABATAN
          const idPegawai = session?.ID_PEGAWAI
          const idUnit = session?.ID_UNIT

          if (idJabatan === '3' || idJabatan === 3) {
            setPegawai(session?.NAMA_PEGAWAI)
            setIdPegawai(session?.ID_PEGAWAI)
            setIdUnit(session?.ID_UNIT)

            cariRegKasir(idPegawai!, idUnit!)
          } else {
            showToastAlert('info', 'Anda bukan kasir')
          }
        } else {
          showToastAlert('info', 'Username atau password salah')
        }
      } else {
        showToastAlert('info', response.data.metadata.message)
      }
    } catch (error) {
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  const clearAllCookies = async () => {
    // Hapus client-side cookies
    const cookies = document.cookie.split('; ')

    cookies.forEach(cookie => {
      const [name] = cookie.split('=')

      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/;`
    })

    console.log('Client-side cookies cleared!')

    // Hapus server-side cookies
    try {
      await axios.get('/api/clear-cookies')
      console.log('Server-side cookies cleared!')
    } catch (error) {
      console.error('Failed to clear server-side cookies:', error)
    }
  }

  const doLoginSession = async (noreg: string = '', url: string = '') => {
    setLoading(true)
    let isurlpublic = ''

    if (url === (process.env.NEXT_PUBLIC_API_URL as string)) {
      isurlpublic = 'true'
    } else {
      isurlpublic = 'false'
    }
    const formData = {
      user: username,
      pass: password
    }

    const encryptedData = await encryptAES(JSON.stringify(formData))

    try {
      const result = await signIn('credentials', {
        redirect: false,
        data: encryptedData,
        noreg: noreg,
        isurlpublic: isurlpublic
      })

      if (result?.error) {
        showToastAlert('info', result.error)
      } else {
        router.push(themeConfig.homePageUrl)
      }
    } catch (error) {
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }

  const cariRegKasir = async (idPegawai: string, idUnit: string) => {
    const url = await getUrlApiLogin()

    try {
      const response = await axios.post(url + 'kasir/cariregkasir', {
        idpegawai: idPegawai,
        idunit: idUnit
      })

      if (response.data.metadata.code === 200) {
        const data = response.data.response.list[0]

        doLoginSession(data?.NOMOR, url as string)
      } else {
        setOpen(true)
      }
    } catch (error) {
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    }
  }

  const handleClose = () => {
    localStorage.removeItem('sessionExpiry')
    setOpen(false)
  }

  const handleShiftChange = (event: any, value: any) => {
    if (value) {
      setselectedShift(value.value)
    } else {
      setselectedShift('')
    }
  }

  const saveRegistrasi = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setLoadingSimpan(true)
    const urlH = await getUrlApiLogin()

    try {
      const url = `${urlH}kasir/saveregkasir`

      const response = await axios.post(url, {
        nomor: null,
        idunit: idUnit,
        tgl: dayjs(tglTransOrder).format('YYYY-MM-DD HH:mm:ss'),
        idpegawai: idPegawai,
        shift: selectedShift || 0,
        saldo: saldo || '0',
        idaktif: '0'
      })

      if (response.data.metadata.code === 200) {
        setOpen(false)
        const data = response.data.response.list[0]

        doLoginSession(data?.NOMOR, urlH as string)
      } else {
        showToastAlert('info', response.data.metadata.message)
      }
    } catch (error) {
      console.error('Error:', error)
      showToastAlert('error', error instanceof Error ? error.message : 'Unknown error')
    } finally {
      setLoadingSimpan(false)
    }
  }

  return (
    <>
      <Dialog fullWidth open={open} onClose={handleClose} maxWidth='sm' scroll='body'>
        <form noValidate autoComplete='off' onSubmit={saveRegistrasi} className='flex flex-col gap-5'>
          <DialogContent className='overflow-visible pbs-0 sm:pbe-2 sm:pli-5'>
            <div className='flex justify-between items-center mb-5 mt-5'>
              <Typography variant='h5'>Registrasi Kasir</Typography>
              <IconButton onClick={handleClose} color='primary' size='small'>
                <i className='ri-close-line' />
              </IconButton>
            </div>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} mt={-1}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={['DateTimePicker']}>
                    <DemoItem>
                      <DateTimePicker
                        readOnly
                        label='Tanggal'
                        sx={{ width: '100%' }}
                        format='YYYY-MM-DD HH:mm'
                        ampm={false}
                        value={tglTransOrder ? dayjs(tglTransOrder) : dayjs()}
                        onChange={newValue => {
                          setTglTransOrder(newValue?.toString() ?? '')
                        }}
                      />
                    </DemoItem>
                  </DemoContainer>
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  disabled
                  InputProps={{
                    readOnly: true
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      textAlign: 'left',
                      color: 'black',
                      opacity: 1
                    },
                    '& .MuiInputBase-root.Mui-disabled': {
                      '& .MuiInputBase-input': {
                        color: 'black'
                      }
                    }
                  }}
                  fullWidth
                  label='Pegawai'
                  onChange={e => setPegawai(e.target.value)}
                  defaultValue={pegawai}
                  placeholder='Pegawai'
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <Autocomplete
                  disableClearable
                  options={dataShift}
                  getOptionLabel={option => option.label}
                  value={
                    dataShift.find(option => option.value === Number(selectedShift)) ?? {
                      label: '',
                      value: 0
                    }
                  }
                  onChange={handleShiftChange}
                  renderInput={params => <TextField {...params} label='Pilih Shift' />}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <TextField
                  fullWidth
                  label='Saldo'
                  onChange={e => setSaldo(e.target.value)}
                  defaultValue={saldo}
                  placeholder='Saldo'
                  InputProps={{
                    inputComponent: NumericFormatCustom as any,
                    inputProps: {
                      style: { textTransform: 'uppercase', textAlign: 'right', opacity: 1 }
                    }
                  }}
                  sx={{
                    '& .MuiInput-input': {
                      textAlign: 'right'
                    }
                  }}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions className='justify-center pbs-0 sm:pbe-6 sm:pli-5'>
            <CustomButton type='submit' loading={loadingSimpan}>
              Simpan
            </CustomButton>
            <CustomButton variant='outlined' type='reset' onClick={handleClose} color='secondary' loading={false}>
              Keluar
            </CustomButton>
          </DialogActions>
        </form>
      </Dialog>
      {!open && (
        <div className='flex bs-full justify-center'>
          <div
            className={classnames(
              'flex bs-full items-center justify-center flex-1 min-bs-[100dvh] relative p-6 max-md:hidden',
              {
                'border-ie': settings.skin === 'bordered'
              }
            )}
          >
            <div className='pli-6 max-lg:mbs-40 lg:mbe-24 transition duration-150 ease-in-out hover:scale-110'>
              <img
                src='/images/icon/login.png'
                alt='logo'
                width={600}
                height={600}
                className='md:block m-auto max-md:w-[75px] max-md:h-[75px]'
              />
            </div>
            <img src='/images/icon/bg_all.webp' alt='' className='absolute inset-0 w-full h-full object-cover z-[-1]' />
            <img src={authBackground} className='absolute bottom-[4%] z-[-1] is-full max-md:hidden' />
          </div>
          <div className='flex justify-center items-center bs-full bg-backgroundPaper !min-is-full p-6 md:!min-is-[unset] md:p-12 md:is-[480px]'>
            <div className='absolute block-start-5 sm:block-start-[10px] inline-start-6 sm:inline-start-[10px]'>
              <LogoLogin />
            </div>
            <div className='flex flex-col gap-5 is-full sm:is-auto md:is-full sm:max-is-[400px] md:max-is-[unset]'>
              <div>
                <Typography variant='h5' className='text-center'>
                  Selamat datang di
                  <strong>{` ${themeConfig.templateName}! 👋🏻`}</strong>
                </Typography>
              </div>
              <form noValidate onSubmit={doLogin} autoComplete='off' className='flex flex-col gap-5'>
                <TextField autoFocus fullWidth label='Username' onChange={e => setUsername(e.target.value)} />
                <TextField
                  fullWidth
                  label='Password'
                  type={isPasswordShown ? 'text' : 'password'}
                  onChange={e => setPassword(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          size='small'
                          edge='end'
                          onClick={handleClickShowPassword}
                          onMouseDown={e => e.preventDefault()}
                        >
                          <i className={isPasswordShown ? 'ri-eye-off-line' : 'ri-eye-line'} />
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%'
                  }}
                >
                  <CustomButton type='submit' loading={loading} color='primary' fullWidth={true}>
                    Log In
                  </CustomButton>
                </Box>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Login
